import { Head, useForm, usePage } from '@inertiajs/react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { trackUmamiEvent } from '@/lib/analytics';
import { inputClass } from '@/lib/formStyles';

interface Profile {
  id: number;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  createdAt: string;
}

interface ProfilePageProps {
  profile: Profile;
  [key: string]: unknown;
}

const labelClass = 'text-on-surface mb-1.5 block text-sm font-medium';
const sectionLabel =
  'text-on-surface-light/70 mb-4 flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-PT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// ── Identity: hero banner + avatar + name/email ───────────────────────────────

function IdentitySection({ profile }: { profile: Profile }) {
  const [avatarPreview, setAvatarPreview] = useState<string | null>(profile.avatarUrl);
  const form = useForm({ name: profile.name ?? '', avatar: null as File | null });
  const { data, setData, processing, errors } = form;

  const displayName = data.name.trim() || profile.email;
  const initial = displayName ? displayName[0].toUpperCase() : '?';

  function handleAvatar(file: File | null) {
    setData('avatar', file);
    setAvatarPreview(file ? URL.createObjectURL(file) : profile.avatarUrl);
  }

  function submit(ev: FormEvent) {
    ev.preventDefault();
    form.transform((d) => {
      const user: { name: string; avatar?: File } = { name: d.name };
      if (d.avatar) user.avatar = d.avatar;
      return { user };
    });
    trackUmamiEvent('admin_profile_update', { has_avatar: !!data.avatar });
    form.patch('/profile', { forceFormData: !!data.avatar, preserveScroll: true });
  }

  return (
    <form onSubmit={submit}>
      {/* Identity */}
      <div className="flex items-center gap-5 px-6 py-6">
        <div className="relative h-28 w-28 shrink-0">
          {avatarPreview ? (
            <img
              src={avatarPreview}
              alt="Foto de perfil"
              className="border-line h-full w-full rounded-full border object-cover"
            />
          ) : (
            <div className="bg-primary font-headline flex h-full w-full items-center justify-center rounded-full text-5xl font-semibold text-white">
              {initial}
            </div>
          )}
          <label
            className="border-line-strong bg-surface text-primary hover:border-primary absolute right-1 bottom-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border transition-colors"
            title="Alterar foto"
          >
            <span className="material-symbols-outlined text-[15px]">photo_camera</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleAvatar(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        <div className="min-w-0">
          <p className="font-headline text-on-surface truncate text-xl font-semibold">
            {displayName}
          </p>
          <p className="text-on-surface-light truncate text-sm">{profile.email}</p>
          <span className="border-line bg-background text-on-surface-light mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px]">
            <span className="material-symbols-outlined text-[13px]">schedule</span>
            Conta criada em {formatDate(profile.createdAt)}
          </span>
        </div>
      </div>

      {/* Dados */}
      <div className="border-line border-t px-6 py-5">
        <p className={sectionLabel}>
          <span className="material-symbols-outlined text-primary text-[16px]">badge</span>
          Dados
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="profile-name">
              Nome
            </label>
            <input
              id="profile-name"
              type="text"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              className={inputClass}
              placeholder="O seu nome"
            />
            {errors.name && <p className="text-error mt-1 text-xs">{errors.name}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="profile-email">
              Email
            </label>
            <input
              id="profile-email"
              type="email"
              value={profile.email}
              readOnly
              className={`${inputClass} bg-background text-on-surface-light cursor-not-allowed`}
            />
          </div>
        </div>
        <p className="text-on-surface-light mt-2 flex items-center gap-1.5 text-xs">
          <span className="material-symbols-outlined text-[13px]">lock</span>O email é o seu nome de
          utilizador e não pode ser alterado aqui.
        </p>
        {errors.avatar && <p className="text-error mt-2 text-xs">{errors.avatar}</p>}
        <div className="mt-5 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={processing}
            loading={processing}
          >
            {processing ? 'A guardar...' : 'Guardar alterações'}
          </Button>
        </div>
      </div>
    </form>
  );
}

// ── Password ──────────────────────────────────────────────────────────────────

function PasswordSection() {
  const form = useForm({ current_password: '', password: '', password_confirmation: '' });
  const { data, setData, processing, errors, reset } = form;
  const [clientError, setClientError] = useState<string | null>(null);

  function submit(ev: FormEvent) {
    ev.preventDefault();
    setClientError(null);
    if (data.password !== data.password_confirmation) {
      setClientError('As palavras-passe não coincidem.');
      return;
    }
    if (data.password.length < 12) {
      setClientError('A nova palavra-passe deve ter pelo menos 12 caracteres.');
      return;
    }
    form.transform((d) => ({ user: d }));
    trackUmamiEvent('admin_profile_password_change');
    form.patch('/profile/password', { preserveScroll: true, onSuccess: () => reset() });
  }

  return (
    <form onSubmit={submit} className="border-line border-t px-6 py-5">
      <p className={sectionLabel}>
        <span className="material-symbols-outlined text-primary text-[16px]">key</span>
        Palavra-passe
      </p>
      <div className="mb-4">
        <label className={labelClass} htmlFor="current-password">
          Palavra-passe atual
        </label>
        <input
          id="current-password"
          type="password"
          autoComplete="current-password"
          value={data.current_password}
          onChange={(e) => setData('current_password', e.target.value)}
          required
          className={inputClass}
        />
        {errors.current_password && (
          <p className="text-error mt-1 text-xs">{errors.current_password}</p>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="new-password">
            Nova palavra-passe
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={data.password}
            onChange={(e) => setData('password', e.target.value)}
            required
            className={inputClass}
          />
          {errors.password && <p className="text-error mt-1 text-xs">{errors.password}</p>}
        </div>
        <div>
          <label className={labelClass} htmlFor="confirm-password">
            Confirmar nova palavra-passe
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={data.password_confirmation}
            onChange={(e) => setData('password_confirmation', e.target.value)}
            required
            className={inputClass}
          />
        </div>
      </div>
      <p className="text-on-surface-light mt-1.5 text-xs">Mínimo 12 caracteres.</p>
      {clientError && <p className="text-error mt-2 text-xs">{clientError}</p>}
      <div className="mt-5 flex justify-end">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={processing}
          loading={processing}
        >
          {processing ? 'A alterar...' : 'Alterar palavra-passe'}
        </Button>
      </div>
    </form>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { profile } = usePage<ProfilePageProps>().props;

  return (
    <>
      <Head title="O meu perfil — Administração" />
      <h1 className="font-headline text-on-surface mb-5 text-2xl font-bold">O meu perfil</h1>
      <div className="mx-auto max-w-2xl">
        <div className="surface-card overflow-hidden">
          <IdentitySection profile={profile} />
          <PasswordSection />
        </div>
      </div>
    </>
  );
}
